import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { CollapsibleSection } from "components/designV2/collapsibleSection/collapsibleSection";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { useCloseOnEscape } from "hooks/useCloseOnEscape";
import { SalaryType } from "model/Salary";
import type { MouseEvent } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBodyScrollLock } from "web-apps/company/components/common/hooks/useBodyScrollLock";
import { STAFF_TAX_TABLE_OPTIONS } from "../shared/staffPanel.options.logic";
import type {
  AddStaffFormData,
  AddStaffPanelProps,
} from "./addStaffPanel.contract";
import { useAddStaffPanelLogic } from "./addStaffPanel.logic";
import {
  CloseButton,
  FieldsGrid,
  Footer,
  PhoneContinueButton,
  PhoneFieldsSpacer,
  HeaderRow,
  Overlay,
  Panel,
  PanelBody,
  SubmitErrorText,
  Title,
} from "./addStaffPanel.styled";

const toMandatoryLabel = (
  label: string,
  translate: (key: string) => string,
): string => {
  return `${label} (${translate("Staff.AddStaffPanelMandatory")})`;
};

export const AddStaffPanel = (
  props: AddStaffPanelProps,
): JSX.Element | null => {
  const { t } = useTranslation();
  const logic = useAddStaffPanelLogic({
    isOpen: props.isOpen,
    onClose: props.onClose,
    onWorkerCreated: props.onWorkerCreated,
    unitTeamOptions: props.unitTeamOptions,
    t,
  });

  useBodyScrollLock(props.isOpen);

  useCloseOnEscape({
    isOpen: props.isOpen && !logic.isSubmitting,
    onClose: props.onClose,
    stopPropagation: true,
  });

  const onOverlayClick = useCallback((): void => {
    if (logic.isSubmitting) return;
    props.onClose();
  }, [logic.isSubmitting, props.onClose]);

  const onPanelClick = useCallback((event: MouseEvent): void => {
    event.stopPropagation();
  }, []);

  const onSetField = useCallback(
    <K extends keyof AddStaffFormData>(
      field: K,
      value: AddStaffFormData[K],
    ): void => {
      logic.setField(field, value);
    },
    [logic.setField],
  );

  if (!props.isOpen) return null;

  return (
    <Overlay onClick={onOverlayClick}>
      <Panel onClick={onPanelClick}>
        <HeaderRow>
          <Title>{t("Staff.AddStaffPanelTitle")}</Title>
          <CloseButton
            iconName="v2/cross"
            size="small"
            aria-label={t("DesignComponents.CloseModal")}
            disabled={logic.isSubmitting}
            onClick={props.onClose}
          />
        </HeaderRow>

        <PanelBody>
          <CollapsibleSection
            label={t("Staff.AddStaffPanelSectionPersonalDetails")}
            defaultOpen
          >
            <FieldsGrid>
              <InputField
                fullWidth
                label={t("Staff.AddStaffPanelFieldPhoneNumberMandatory")}
                hintText={t("Staff.AddStaffPanelHintPhoneNumberCountryCode")}
                maxLength={15}
                value={logic.formData.phoneNumber}
                errorText={logic.phoneError}
                hasError={logic.phoneError.length > 0}
                onChange={logic.onPhoneNumberChange}
                onClear={() => logic.onPhoneNumberChange("")}
                onBlur={logic.onPhoneNumberBlur}
              />
              <PhoneContinueButton
                label={t("General.Continue")}
                size="small"
                disabled={
                  logic.isSubmitting ||
                  logic.isCheckingPhoneNumber ||
                  logic.formData.phoneNumber.trim().length === 0
                }
                isLoading={logic.isCheckingPhoneNumber}
                onClick={logic.onPhoneNumberContinue}
              />
              <PhoneFieldsSpacer />
              <InputField
                fullWidth
                label={t("Staff.AddStaffPanelFieldFirstNameMandatory")}
                value={logic.formData.firstName}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("firstName", value)}
              />
              <InputField
                fullWidth
                label={t("Staff.AddStaffPanelFieldLastNameMandatory")}
                value={logic.formData.lastName}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("lastName", value)}
              />
              <InputField
                fullWidth
                label={t("Staff.AddStaffPanelFieldEmail")}
                value={logic.formData.email}
                type="email"
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("email", value)}
              />
              <InputField
                fullWidth
                label={t("Staff.AddStaffPanelFieldPersonalNumber")}
                hintText={t("Staff.AddStaffPanelHintPersonalNumberFormat")}
                value={logic.formData.personalIdentityNumber}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) =>
                  onSetField("personalIdentityNumber", value)
                }
              />
              <SearchableDropdown
                fullWidth
                label={toMandatoryLabel(t("Staff.AddStaffPanelFieldTeam"), t)}
                options={logic.teamOptions}
                selectedValues={logic.formData.teamIds}
                multiSelect
                disabled={logic.areFormFieldsDisabled}
                onSelectedValuesChange={(value) => onSetField("teamIds", value)}
                placeholder={t("Staff.AddStaffPanelPlaceholderNotAssigned")}
              />
            </FieldsGrid>
          </CollapsibleSection>

          <CollapsibleSection
            label={t("Staff.AddStaffPanelSectionEmployment")}
            defaultOpen
          >
            <FieldsGrid>
              <Dropdown
                fullWidth
                label={toMandatoryLabel(
                  t("Staff.AddStaffPanelFieldEmploymentType"),
                  t,
                )}
                options={logic.employmentOptions}
                value={logic.formData.employmentType}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("employmentType", value)}
                placeholder={t("Staff.AddStaffPanelPlaceholderEmploymentType")}
              />
              <InputField
                fullWidth
                label={
                  logic.validationRequirements.isEmployeeNumberRequired
                    ? toMandatoryLabel(
                        t("Staff.AddStaffPanelFieldEmployeeNumber"),
                        t,
                      )
                    : t("Staff.AddStaffPanelFieldEmployeeNumber")
                }
                value={logic.formData.employeeNumber}
                iconName="v2/reload"
                iconSize="18px"
                iconAriaLabel={t(
                  "Staff.AddStaffPanelActionReloadEmployeeNumber",
                )}
                onIconClick={logic.fetchEmployeeNumber}
                disabled={
                  logic.isLoadingEmployeeNumber ||
                  logic.isSubmitting ||
                  logic.areFormFieldsDisabled
                }
                onChange={(value) => onSetField("employeeNumber", value)}
              />
              <InputField
                fullWidth
                label={toMandatoryLabel(t("Staff.AddStaffPanelFieldRole"), t)}
                value={logic.formData.role}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("role", value)}
              />
              <Dropdown
                fullWidth
                label={
                  logic.validationRequirements.isSalaryModelRequired
                    ? toMandatoryLabel(
                        t("Staff.AddStaffPanelFieldSalaryModel"),
                        t,
                      )
                    : t("Staff.AddStaffPanelFieldSalaryModel")
                }
                options={logic.salaryOptions}
                value={logic.formData.salaryType}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("salaryType", value)}
                placeholder={t("Staff.AddStaffPanelPlaceholderSalaryModel")}
              />
              <InputField
                fullWidth
                type="number"
                label={
                  logic.validationRequirements.isSalaryAmountRequired
                    ? toMandatoryLabel(logic.salaryAmountLabel, t)
                    : logic.salaryAmountLabel
                }
                value={logic.formData.salaryAmount}
                placeholder={t("Staff.AddStaffPanelPlaceholderSalaryAmount")}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("salaryAmount", value)}
              />
              {logic.formData.salaryType === SalaryType.Monthly && (
                <InputField
                  fullWidth
                  type="number"
                  label={
                    logic.validationRequirements.isWorkPercentageRequired
                      ? toMandatoryLabel(
                          `${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`,
                          t,
                        )
                      : `${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`
                  }
                  value={logic.formData.workPercentage}
                  disabled={logic.areFormFieldsDisabled}
                  onChange={(value) => onSetField("workPercentage", value)}
                />
              )}
              <Dropdown
                fullWidth
                label={
                  logic.validationRequirements.isTaxTableRequired
                    ? toMandatoryLabel(t("Staff.AddStaffPanelFieldTaxTable"), t)
                    : t("Staff.AddStaffPanelFieldTaxTable")
                }
                options={STAFF_TAX_TABLE_OPTIONS}
                value={logic.formData.taxTable}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("taxTable", value)}
              />
              <Dropdown
                fullWidth
                label={
                  logic.validationRequirements.isPaymentTypeRequired
                    ? toMandatoryLabel(
                        t("Staff.AddStaffPanelFieldPaymentSolution"),
                        t,
                      )
                    : t("Staff.AddStaffPanelFieldPaymentSolution")
                }
                options={logic.paymentOptions}
                value={logic.formData.paymentType}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("paymentType", value)}
              />
              <Dropdown
                fullWidth
                label={
                  logic.validationRequirements.isEmployeeCategoryRequired
                    ? toMandatoryLabel(
                        t("Staff.AddStaffPanelFieldStaffType"),
                        t,
                      )
                    : t("Staff.AddStaffPanelFieldStaffType")
                }
                options={logic.employeeCategoryOptions}
                value={logic.formData.employeeCategory}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("employeeCategory", value)}
              />
              <DatePicker
                label={toMandatoryLabel(
                  t("Staff.AddStaffPanelFieldStartDate"),
                  t,
                )}
                value={logic.formData.startDate}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("startDate", value)}
                allowClear
              />
              <DatePicker
                label={
                  logic.validationRequirements.isEndDateRequired
                    ? toMandatoryLabel(t("Staff.AddStaffPanelFieldEndDate"), t)
                    : t("Staff.AddStaffPanelFieldEndDate")
                }
                value={logic.formData.endDate}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("endDate", value)}
                allowClear
              />
            </FieldsGrid>
          </CollapsibleSection>

          <CollapsibleSection
            label={t("Staff.AddStaffPanelSectionAgreementsTerms")}
            defaultOpen
          >
            <FieldsGrid>
              <InputField
                fullWidth
                label={t("Staff.AddStaffPanelFieldCollectiveSalaryAgreement")}
                value={logic.formData.agreement}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("agreement", value)}
              />
              <InputField
                fullWidth
                type="number"
                label={t("Staff.AddStaffPanelFieldNoticePeriodDays")}
                value={logic.noticeDaysInputController.value}
                disabled={logic.areFormFieldsDisabled}
                onChange={logic.noticeDaysInputController.onChange}
                onBlur={logic.noticeDaysInputController.onBlur}
              />
              <InputField
                fullWidth
                type="number"
                label={t("Staff.AddStaffPanelFieldVacationDays")}
                value={logic.vacationDaysInputController.value}
                disabled={logic.areFormFieldsDisabled}
                onChange={logic.vacationDaysInputController.onChange}
                onBlur={logic.vacationDaysInputController.onBlur}
              />
              <InputField
                fullWidth
                label={t("Staff.AddStaffPanelFieldWorkPlace")}
                value={logic.formData.workplace}
                disabled={logic.areFormFieldsDisabled}
                onChange={(value) => onSetField("workplace", value)}
              />
            </FieldsGrid>
          </CollapsibleSection>
        </PanelBody>

        <Footer>
          {logic.submitError.length > 0 ? (
            <SubmitErrorText role="alert">{logic.submitError}</SubmitErrorText>
          ) : null}
          <PrimaryButton
            label={t("Staff.AddStaffPanelActionAddWorker")}
            disabled={logic.isCreateDisabled}
            isLoading={logic.isSubmitting}
            onClick={logic.onAddWorkerClick}
          />
        </Footer>
      </Panel>
    </Overlay>
  );
};
